import { useQuery } from '@tanstack/react-query';
import { useEffect, useState } from 'react';

import { getCurrencyWarningsApi } from '@flipster/apis/v1/currency';
import { getLocalStorage } from '@flipster/utils/localStorage';

import { CurrencyWarningAlert } from '../shared/alert/currency-warning';
import { MemoAlert } from '../shared/alert/memo-warning';
import { getCurrencyWarningStorageKey } from '../shared/storage-key';

interface Props {
  userId: string;
  selectedCurrency: string | null;
  selectedBlockchainName: string | undefined;
  selectedNetwork: string;
  destinationTag: string | undefined;
  memo: string | undefined;
  isLoading: boolean;
}

/**
 * Alert 우선순위
 * 1. CurrencyWarningAlert
 * 2. MemoAlert
 */
export const AlertHub = ({
  userId,
  selectedCurrency,
  selectedBlockchainName,
  selectedNetwork,
  destinationTag,
  memo,
  isLoading,
}: Props) => {
  const [isClosedCurrencyWarning, setIsClosedCurrencyWarning] = useState(false);
  const [isClosedMemoAlert, setIsClosedMemoAlert] = useState(false);

  const currentPageWarningQuery = useQuery({
    queryKey: ['getCurrencyWarnings'],
    queryFn: getCurrencyWarningsApi,
    select: ({ data }) =>
      data.warnings.find(
        (warning) =>
          warning.type === 'CURRENCY_WARNING_TYPE_DEPOSIT' &&
          warning.currency.toLowerCase() === selectedCurrency?.toLowerCase() &&
          warning.blockchainName?.toLowerCase() === selectedBlockchainName?.toLowerCase(),
      ),
  });

  const storageKey =
    currentPageWarningQuery.data &&
    getCurrencyWarningStorageKey({
      userId,
      warningData: currentPageWarningQuery.data,
    });

  const isSkipCurrencyWarning = storageKey && getLocalStorage(storageKey);
  const isShowCurrencyWarning =
    currentPageWarningQuery.isSuccess && currentPageWarningQuery.data && !isSkipCurrencyWarning;
  const isShowMemoAlert = !isLoading && userId && selectedNetwork && (destinationTag || memo);

  useEffect(() => {
    // if selected network is changed, reset isClosedCurrencyWarning and isClosedMemoAlert
    if (selectedNetwork) {
      setIsClosedCurrencyWarning(false);
      setIsClosedMemoAlert(false);
    }
  }, [selectedNetwork]);

  if (isShowCurrencyWarning && !isClosedCurrencyWarning) {
    return (
      <CurrencyWarningAlert
        key={currentPageWarningQuery.data!.blockchainName}
        warningData={currentPageWarningQuery.data!}
        onClose={() => {
          setIsClosedCurrencyWarning(true);
        }}
      />
    );
  }
  if (isShowMemoAlert && !isClosedMemoAlert) {
    return (
      <MemoAlert
        key={selectedNetwork}
        userId={userId}
        required={destinationTag ? 'Tag' : 'Memo'}
        currency={selectedCurrency ?? ''}
        network={selectedNetwork}
        onClose={() => {
          setIsClosedMemoAlert(true);
        }}
      />
    );
  }
  return null;
};
